/*
  ヘッダー検索窓の候補表示（日本語対応の自作版）
  Shopify標準の予測検索API(/search/suggest)は日本語ストア非対応（417 Unsupported buyer locale）のため、
  通常の検索結果を Section Rendering API（sections/search-suggest.liquid）で取得して
  入力欄の下にドロップダウン表示する。search-form.js の SearchForm（×ボタン制御・入力のdebounce）を継承。
*/
class SearchSuggest extends SearchForm {
  constructor() {
    super();
    this.panel = this.querySelector('[data-search-suggest]');
    this.status = this.querySelector('[data-search-suggest-status]');
    this.sectionId = this.dataset.sectionId || 'search-suggest';
    // 同じページに複数の検索窓（PC用とスマホ用）があるため、候補内の id をインスタンスごとに分ける
    this.instance = (this.dataset.instance || 'search').replace(/[^A-Za-z0-9_-]/g, '');
    this.minLength = parseInt(this.dataset.minLength || '1', 10);
    this.cache = new Map();
    this.abortController = null;
    this.searchTerm = '';
    this.renderedTerm = '';
    this.isOpen = false;

    if (!this.input || !this.panel) return;

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    // パネル内をクリックしても入力欄のフォーカスを外さない（focusoutで閉じてクリックが消えるのを防ぐ）
    this.panel.addEventListener('mousedown', (event) => event.preventDefault());
    this.onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.close();
    };
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    if (this.abortController) this.abortController.abort();
  }

  getQuery() {
    return this.input.value.trim();
  }

  // 検索ページと同じ action（ロケール接頭辞付きでも追従）
  getSearchUrl() {
    return (
      this.input.form.getAttribute('action') ||
      (window.routes && window.routes.search_url) ||
      '/search'
    );
  }

  // SearchForm が input を debounce(300ms) して呼ぶ
  onChange() {
    super.onChange();
    const term = this.getQuery();
    this.searchTerm = term;
    this.clearSelection();
    if (term.length < this.minLength) {
      if (this.abortController) this.abortController.abort();
      this.removeAttribute('loading');
      this.clearPanel();
      this.close();
      return;
    }
    this.fetchResults(term);
  }

  // Dawn の SearchForm は「候補が選択中なら reset しない」が、ここでは Enter を onFormSubmit で処理するので常に reset する
  shouldResetForm() {
    return true;
  }

  onFormReset(event) {
    this.clearSelection();
    super.onFormReset(event);
    this.searchTerm = '';
    if (this.abortController) this.abortController.abort();
    this.removeAttribute('loading');
    this.clearPanel();
    this.close();
  }

  onFocus() {
    const term = this.getQuery();
    if (term.length >= this.minLength && term === this.renderedTerm && this.panel.innerHTML.trim()) {
      this.open();
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  fetchResults(term) {
    if (this.cache.has(term)) {
      this.render(term, this.cache.get(term));
      return;
    }
    if (this.abortController) this.abortController.abort();
    const controller = new AbortController();
    this.abortController = controller;
    this.setAttribute('loading', '');

    const url = `${this.getSearchUrl()}?q=${encodeURIComponent(term)}&type=product&options%5Bprefix%5D=last&section_id=${encodeURIComponent(this.sectionId)}`;

    fetch(url, { signal: controller.signal })
      .then((response) => {
        if (!response.ok || response.redirected) throw new Error(`search suggest: HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const section = doc.getElementById(`shopify-section-${this.sectionId}`);
        if (!section) throw new Error('search suggest: section not found');
        const inner = section.innerHTML;
        this.cache.set(term, inner);
        if (term === this.searchTerm) this.render(term, inner);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.clearPanel();
        this.close();
      })
      .finally(() => {
        if (controller === this.abortController) this.removeAttribute('loading');
      });
  }

  render(term, html) {
    this.clearSelection();
    this.panel.innerHTML = html;
    this.panel.querySelectorAll('[id^="search-suggest-"]').forEach((element) => {
      element.id = element.id.replace(/^search-suggest-/, `search-suggest-${this.instance}-`);
    });
    this.input.setAttribute('aria-controls', `search-suggest-${this.instance}-listbox`);
    this.renderedTerm = term;
    this.announce();
    // 取得完了時に入力欄からフォーカスが外れていたら開かない（次に戻ってきた時 onFocus で開く）
    if (html.trim() && this.contains(document.activeElement)) {
      this.open();
    } else {
      this.close();
    }
  }

  clearPanel() {
    this.panel.innerHTML = '';
    this.renderedTerm = '';
    this.announce();
  }

  // 読み上げ用：セクションが出力する件数テキストを status に反映
  announce() {
    if (!this.status) return;
    const source = this.panel.querySelector('[data-search-suggest-status]');
    this.status.textContent = source ? source.textContent.trim() : '';
  }

  open() {
    this.setAttribute('open', '');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close() {
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.clearSelection();
    this.isOpen = false;
  }

  getOptions() {
    return Array.from(this.panel.querySelectorAll('[role="option"]'));
  }

  clearSelection() {
    this.getOptions().forEach((element) => element.setAttribute('aria-selected', 'false'));
    this.input.removeAttribute('aria-activedescendant');
  }

  onKeydown(event) {
    // 日本語入力の変換中（候補選択に矢印キーを使う）は何もしない
    if (event.isComposing || event.keyCode === 229) return;

    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    if (!this.isOpen) {
      // 閉じた状態で↓を押したら、同じ語の候補があれば開き直す
      if (event.key === 'ArrowDown' && this.getQuery() === this.renderedTerm && this.panel.innerHTML.trim()) {
        event.preventDefault();
        this.open();
      }
      return;
    }

    const options = this.getOptions();
    if (!options.length) return;
    event.preventDefault();

    const current = options.findIndex((element) => element.getAttribute('aria-selected') === 'true');
    let next = event.key === 'ArrowDown' ? current + 1 : current - 1;
    if (next >= options.length) next = 0;
    if (next < 0) next = options.length - 1;

    this.clearSelection();
    options[next].setAttribute('aria-selected', 'true');
    if (options[next].id) this.input.setAttribute('aria-activedescendant', options[next].id);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) {
      event.preventDefault();
      return;
    }
    if (!this.isOpen) return;
    const selected = this.panel.querySelector('[aria-selected="true"]');
    if (!selected) return;
    const link = selected.matches('a') ? selected : selected.querySelector('a');
    if (link && link.href) {
      event.preventDefault();
      window.location.href = link.href;
    }
  }
}

customElements.define('search-suggest', SearchSuggest);


/*
  スマホ用: ヘッダーの虫眼鏡ボタンで検索バー(#SearchSp)を開閉する。
  開いた時に入力欄へフォーカス（タップ操作の中で呼ぶのでiOSでもキーボードが出る）。
*/
class SearchSpToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    const targetId = this.button && this.button.getAttribute('aria-controls');
    this.panel = targetId ? document.getElementById(targetId) : null;
    if (!this.button || !this.panel) return;
    this.button.addEventListener('click', () => this.toggle());
    this.panel.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !event.isComposing) this.close();
    });
    // バーの外（候補パネル含む自分の外側）をタップしたら閉じる
    document.addEventListener('click', (event) => {
      if (this.panel.hidden) return;
      if (this.contains(event.target) || this.panel.contains(event.target)) return;
      this.close(false);
    });
  }

  toggle() {
    if (this.panel.hidden) {
      this.open();
    } else {
      this.close();
    }
  }

  open() {
    this.panel.hidden = false;
    this.button.setAttribute('aria-expanded', 'true');
    const input = this.panel.querySelector('input[type="search"]');
    if (input) input.focus();
  }

  close(returnFocus = true) {
    this.panel.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
    if (returnFocus) this.button.focus();
  }
}

customElements.define('search-sp-toggle', SearchSpToggle);
